import type React from 'react';
import {useMemo} from 'react';
import {
  SidebarGlobalSettingsLinksConfig,
  SidebarInstanceSettingsLinksConfig,
  SidebarUserLinksConfig,
} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import type {SidebarLinkItemProps} from 'src/app/layout/Sidebar/components/SidebarLinks';
import {directoryLinks} from 'src/app/layout/Sidebar/components/SidebarLinks';
import {
  isSidebarLinkVisible,
  useSidebarLinkVisibilityContext,
} from 'src/app/layout/Sidebar/useVisibleSidebarLinks';

export interface SearchNavAction {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface NavLinkSource {
  link: SidebarLinkItemProps;
  label: string;
}

// subLinks flatten to their children. Directory pages carry the directory
// name because System and Settings both list "Compliance Standards".
const NAV_LINK_SOURCES: NavLinkSource[] = [
  ...SidebarUserLinksConfig,
  ...SidebarInstanceSettingsLinksConfig,
  ...SidebarGlobalSettingsLinksConfig,
].flatMap(link => {
  if (link.subLinks) {
    return link.subLinks.map(sub => ({link: sub, label: `Go to ${sub.name}`}));
  }
  if (link.directory) {
    return [
      {link, label: `Go to ${link.name}`},
      ...directoryLinks(link.directory).map(page => ({
        link: page,
        label: `Go to ${link.name} › ${page.name}`,
      })),
    ];
  }
  return [{link, label: `Go to ${link.name}`}];
});

export const useSearchNavActions = (): SearchNavAction[] => {
  const ctx = useSidebarLinkVisibilityContext();

  return useMemo(() => {
    return NAV_LINK_SOURCES.filter(({link}) =>
      isSidebarLinkVisible(link, ctx)
    ).map(({link, label}) => ({
      id: `nav-${link.path}`,
      label,
      path: link.path,
      icon: link.icon,
    }));
  }, [ctx]);
};
